import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useMemo } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';

import { ROUTE } from '~/app/routes';
import { Details } from '~/components';
import { useNavigate } from '~/lib/navigation';
import { CardTransactionRepository } from '~/repositories/CardTransactionRepository';
import { useNotificationStore } from '~/stores/Notification';
import { Inner } from '~/styles';

import { CardTransactionProps } from './CardTransaction';

interface Params {
  id: string;
}

const cardTransactionQueryKey = (id: string) => ['card-transaction', id] as const;

export const PageCardTransactionApiResponse: React.FC = () => {
  const { addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const { id } = useParams<keyof Params>() as Params;
  const { cardTransaction: outletCardTransaction } = useOutletContext<CardTransactionProps>();

  const { data: cardTransaction, error: cardTransactionError } = useQuery({
    queryKey: cardTransactionQueryKey(id!),
    queryFn: () => CardTransactionRepository.get(id!),
    enabled: !!id && !outletCardTransaction,
    initialData: outletCardTransaction,
  });

  useEffect(() => {
    if (cardTransactionError) {
      navigate(ROUTE.CARD_TRANSACTIONS);
      addDangerNotification({
        content: (cardTransactionError as any).message || 'Failed to load card transaction',
      });
    }
  }, [cardTransactionError, navigate, addDangerNotification]);

  const formattedCardTransaction = useMemo(
    () => (cardTransaction ? (cardTransaction as unknown as Record<string, unknown>) : undefined),
    [cardTransaction]
  );

  if (!cardTransaction) {
    return null;
  }

  return (
    <Inner pt={24} px={0} pb={0}>
      <Details
        label="API Response"
        formattedData={formattedCardTransaction}
        rawData={cardTransaction as unknown as Record<string, unknown> | undefined}
      />
    </Inner>
  );
};
